/**
 * ItemSummaryReport — Read-only report showing raw materials and finished products
 * with yield factors, labor factors (placeholder), brand, location, and last-used/produced dates.
 * Rendered as a tab inside the Reports page.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import productionService from '../../services/productionService';
import { formatYield, getYieldColor } from '../../hooks/useYieldFactor';
import { formatShortDate } from '../../utils/dateUtils';
import { Search, ChevronDown, ChevronUp, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Calendar } from 'lucide-react';
import SearchableDropdown from '../../components/SearchableDropdown';
import DatePickerDropdown from '../../components/DatePickerDropdown';

interface ItemSummaryReportProps {
  embedded?: boolean;
}

type SubView = 'raw-materials' | 'finished-products';

interface RawMaterialItem {
  item_number: string;
  product_item_number: string;  // the finished product this (material, yield) belongs to
  item_english_name: string;
  item_chinese_name: string;
  english_brand_name: string | null;
  chinese_brand_name: string | null;
  location_code: string | null;
  unit_weight: number | null;
  packing: string | null;
  yield_factor: number | null;  // product-specific; null when ics.raw_material yield is unset
  yield_factor_source: string | null;
  labor_factor: number | null;
  last_used_date: string | null;
}

interface FinishedProductItem {
  item_number: string;
  item_english_name: string;
  item_chinese_name: string;
  english_brand_name: string | null;
  chinese_brand_name: string | null;
  location_code: string | null;
  unit_weight: number | null;
  packing: string | null;
  last_produced_date: string | null;
  materials: Array<{
    item_number: string;
    material_name: string;
    yield_factor: number;
    yield_factor_source: string;
    labor_factor: number | null;
  }>;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface Brand {
  english_brand_name: string;
  chinese_brand_name: string | null;
}

const ItemSummaryReport: React.FC<ItemSummaryReportProps> = () => {
  const { t } = useLanguage();

  const [subView, setSubView] = useState<SubView>('raw-materials');
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');
  const [location, setLocation] = useState('');

  // Sorting state
  const [sortField, setSortField] = useState<string>('item_number');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);

  const [rawMaterials, setRawMaterials] = useState<RawMaterialItem[]>([]);
  const [finishedProducts, setFinishedProducts] = useState<FinishedProductItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Brand options for SearchableDropdown
  const brandOptions = useMemo(() =>
    brands
      .filter((b, i, arr) => arr.findIndex(x => x.english_brand_name === b.english_brand_name) === i)
      .map(b => ({ value: b.english_brand_name, label: b.english_brand_name })),
    [brands]
  );

  // Load brands for dropdown
  useEffect(() => {
    productionService.getItemSummaryBrands()
      .then((data: { brands: Brand[] }) => setBrands(data.brands || []))
      .catch(() => { console.warn('Failed to load brands for item summary filter'); });
  }, []);

  // Fetch data when filters change
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: debouncedSearch || undefined,
        brand: brand || undefined,
        location: location || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page,
        limit: 25,
      };

      if (subView === 'raw-materials') {
        const data = await productionService.getItemSummaryRawMaterials(params);
        setRawMaterials(data.items || []);
        setPagination(data.pagination);
      } else {
        const data = await productionService.getItemSummaryFinished(params);
        setFinishedProducts(data.items || []);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch item summary:', err);
    } finally {
      setLoading(false);
    }
  }, [subView, debouncedSearch, brand, location, startDate, endDate, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [subView, debouncedSearch, brand, location, startDate, endDate]);

  const toggleExpand = (itemNumber: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(itemNumber)) next.delete(itemNumber);
      else next.add(itemNumber);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* Filters bar — segmented toggle + search + dropdowns + dates */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Segmented toggle: Raw Materials / Finished Products */}
          <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-700 p-0.5">
            <button
              onClick={() => setSubView('raw-materials')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                subView === 'raw-materials'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t('itemSummaryReport.rawMaterials')}
            </button>
            <button
              onClick={() => setSubView('finished-products')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                subView === 'finished-products'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {t('itemSummaryReport.finishedProducts')}
            </button>
          </div>

          {/* Divider */}
          <div className="w-px h-7 bg-gray-200 dark:bg-gray-600" />

          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('itemSummaryReport.search')}
              className="w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Brand searchable dropdown */}
          <SearchableDropdown
            options={brandOptions}
            value={brand}
            onChange={setBrand}
            placeholder={t('itemSummaryReport.allBrands')}
            className="w-44"
          />

          {/* Location dropdown */}
          <select
            value={location}
            onChange={e => setLocation(e.target.value)}
            className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('itemSummaryReport.allLocations')}</option>
            <option value="M">M (Main)</option>
            <option value="G">G</option>
            <option value="X">X (Chinatown)</option>
          </select>

          {/* Date range */}
          <DatePickerDropdown
            selected={startDate || null}
            onSelect={(date) => setStartDate(date)}
          >
            {({ onClick }) => (
              <button type="button" onClick={onClick} className="flex items-center gap-2 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" title={t('itemSummaryReport.startDate')}>
                {startDate ? (() => { const d = new Date(startDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Start date...'}
                <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
              </button>
            )}
          </DatePickerDropdown>
          <DatePickerDropdown
            selected={endDate || null}
            onSelect={(date) => setEndDate(date)}
          >
            {({ onClick }) => (
              <button type="button" onClick={onClick} className="flex items-center gap-2 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" title={t('itemSummaryReport.endDate')}>
                {endDate ? (() => { const d = new Date(endDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'End date...'}
                <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
              </button>
            )}
          </DatePickerDropdown>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-3" />
            Loading...
          </div>
        ) : subView === 'raw-materials' ? (
          <RawMaterialsTable items={rawMaterials} t={t} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
        ) : (
          <FinishedProductsTable
            items={finishedProducts}
            expandedRows={expandedRows}
            toggleExpand={toggleExpand}
            t={t}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
          />
        )}

        {/* Empty state */}
        {!loading && (subView === 'raw-materials' ? rawMaterials : finishedProducts).length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <p className="text-lg font-medium">{t('itemSummaryReport.noData')}</p>
            <p className="text-sm mt-1">{t('itemSummaryReport.noDataHint')}</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            {t('itemSummaryReport.showing')} {((page - 1) * 25) + 1}–{Math.min(page * 25, pagination.total)} {t('itemSummaryReport.of')} {pagination.total} {t('itemSummaryReport.items')}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1">
              {t('itemSummaryReport.page')} {page} {t('itemSummaryReport.of')} {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(pagination.totalPages)}
              disabled={page === pagination.totalPages}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Sortable table header cell */
function SortableHeader({ field, label, sortField, sortDir, onSort }: {
  field: string; label: string; sortField: string; sortDir: 'asc' | 'desc'; onSort: (field: string) => void;
}) {
  const isActive = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      className="px-4 py-3 font-medium text-gray-600 dark:text-gray-300 cursor-pointer select-none hover:text-gray-900 dark:hover:text-white transition-colors"
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (
          sortDir === 'asc'
            ? <ChevronUp className="w-3.5 h-3.5" />
            : <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 opacity-0 group-hover:opacity-30" />
        )}
      </div>
    </th>
  );
}

/** Generic sorter for table rows */
function sortItems<T>(items: T[], field: string, dir: 'asc' | 'desc'): T[] {
  return [...items].sort((a, b) => {
    const av = (a as unknown as Record<string, unknown>)[field];
    const bv = (b as unknown as Record<string, unknown>)[field];
    // Nulls last
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    // Numbers
    if (typeof av === 'number' && typeof bv === 'number') {
      return dir === 'asc' ? av - bv : bv - av;
    }
    // Strings
    const sa = String(av).toLowerCase();
    const sb = String(bv).toLowerCase();
    return dir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
  });
}

/** Raw Materials data table */
function RawMaterialsTable({ items, t, sortField, sortDir, onSort }: {
  items: RawMaterialItem[]; t: (key: string) => string;
  sortField: string; sortDir: 'asc' | 'desc'; onSort: (field: string) => void;
}) {
  if (items.length === 0) return null;

  // Map sort field to data key
  const fieldMap: Record<string, string> = {
    item_number: 'item_number',
    item_name: 'item_english_name',
    brand: 'english_brand_name',
    location: 'location_code',
    yield_factor: 'yield_factor',
    labor_factor: 'labor_factor',
    last_used: 'last_used_date',
  };
  const sorted = sortItems(items, fieldMap[sortField] || 'item_number', sortDir);

  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 dark:bg-gray-700/50 text-left">
        <tr>
          <SortableHeader field="item_number" label={t('itemSummaryReport.itemNumber')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="item_name" label={t('itemSummaryReport.itemName')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="brand" label={t('itemSummaryReport.brand')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="location" label={t('itemSummaryReport.location')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="yield_factor" label={t('itemSummaryReport.yieldFactor')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="labor_factor" label={t('itemSummaryReport.laborFactor')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="last_used" label={t('itemSummaryReport.lastUsed')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {sorted.map(item => (
          <tr key={`${item.item_number}|${item.product_item_number}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 text-gray-900 dark:text-gray-200">
            <td className="px-4 py-3 font-mono text-xs">
              {item.item_number}
              {item.product_item_number && (
                <sub className="ml-1 text-[10px] text-gray-400 dark:text-gray-500">({item.product_item_number})</sub>
              )}
            </td>
            <td className="px-4 py-3">
              <div>{item.item_english_name}</div>
              {item.item_chinese_name && (
                <div className="text-xs text-gray-500 dark:text-gray-400">{item.item_chinese_name}</div>
              )}
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.english_brand_name || '—'}</td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.location_code || '—'}</td>
            <td className="px-4 py-3">
              {item.yield_factor == null ? (
                <span className="text-gray-400 dark:text-gray-500">—</span>
              ) : (
                <span className={`font-medium ${getYieldColor(item.yield_factor)}`}>
                  {formatYield(item.yield_factor, 2, item.yield_factor_source)}
                </span>
              )}
            </td>
            <td className="px-4 py-3 text-gray-400 dark:text-gray-500 italic">
              {item.labor_factor != null ? item.labor_factor.toFixed(2) : t('itemSummaryReport.laborPlaceholder')}
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
              {item.last_used_date ? formatShortDate(item.last_used_date) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Finished Products data table with expandable material rows */
function FinishedProductsTable({
  items, expandedRows, toggleExpand, t, sortField, sortDir, onSort,
}: {
  items: FinishedProductItem[];
  expandedRows: Set<string>;
  toggleExpand: (itemNumber: string) => void;
  t: (key: string) => string;
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
}) {
  if (items.length === 0) return null;

  const fieldMap: Record<string, string> = {
    item_number: 'item_number',
    item_name: 'item_english_name',
    brand: 'english_brand_name',
    location: 'location_code',
    materials: 'materials',
    last_produced: 'last_produced_date',
  };
  // Sort, with special handling for materials count
  const sorted = [...items].sort((a, b) => {
    const key = fieldMap[sortField] || 'item_number';
    let av: unknown, bv: unknown;
    if (key === 'materials') {
      av = a.materials.length;
      bv = b.materials.length;
    } else {
      av = (a as unknown as Record<string, unknown>)[key];
      bv = (b as unknown as Record<string, unknown>)[key];
    }
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    const sa = String(av).toLowerCase(), sb = String(bv).toLowerCase();
    return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
  });

  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 dark:bg-gray-700/50 text-left">
        <tr>
          <th className="px-4 py-3 w-8"></th>
          <SortableHeader field="item_number" label={t('itemSummaryReport.itemNumber')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="item_name" label={t('itemSummaryReport.itemName')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="brand" label={t('itemSummaryReport.brand')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="location" label={t('itemSummaryReport.location')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <SortableHeader field="materials" label={t('itemSummaryReport.materials')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
          <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">{t('itemSummaryReport.laborFactor')}</th>
          <SortableHeader field="last_produced" label={t('itemSummaryReport.lastProduced')} sortField={sortField} sortDir={sortDir} onSort={onSort} />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {sorted.map(item => {
          const isExpanded = expandedRows.has(item.item_number);
          const hasMaterials = item.materials.length > 0;
          return (
            <React.Fragment key={item.item_number}>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30 text-gray-900 dark:text-gray-200">
                <td className="px-4 py-3">
                  {hasMaterials && (
                    <button
                      onClick={() => toggleExpand(item.item_number)}
                      className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 text-gray-400" />
                        : <ChevronRight className="w-4 h-4 text-gray-400" />
                      }
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{item.item_number}</td>
                <td className="px-4 py-3">
                  <div>{item.item_english_name}</div>
                  {item.item_chinese_name && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">{item.item_chinese_name}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.english_brand_name || '—'}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{item.location_code || '—'}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                  {hasMaterials ? `${item.materials.length} material${item.materials.length > 1 ? 's' : ''}` : '—'}
                </td>
                <td className="px-4 py-3 text-gray-400 dark:text-gray-500 italic">
                  {t('itemSummaryReport.laborPlaceholder')}
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                  {item.last_produced_date ? formatShortDate(item.last_produced_date) : '—'}
                </td>
              </tr>
              {/* Expanded material rows */}
              {isExpanded && item.materials.map(mat => (
                <tr key={`${item.item_number}-${mat.item_number}`} className="bg-gray-50/50 dark:bg-gray-700/20">
                  <td className="px-4 py-2"></td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500 dark:text-gray-400 pl-8">{mat.item_number}</td>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{mat.material_name}</td>
                  <td className="px-4 py-2"></td>
                  <td className="px-4 py-2"></td>
                  <td className="px-4 py-2">
                    <span className={`font-medium ${getYieldColor(mat.yield_factor)}`}>
                      {formatYield(mat.yield_factor, 2, mat.yield_factor_source)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-400 dark:text-gray-500 italic">
                    {mat.labor_factor != null ? mat.labor_factor.toFixed(2) : '—'}
                  </td>
                  <td className="px-4 py-2"></td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

export default ItemSummaryReport;
